import { useEffect, useState } from 'react';
import { Alert, Button, Chip, EmptyState, Page, PageHeader, Spinner } from '@nexvia-logistics/ui';
import { Plus } from '@nexvia-logistics/ui/icons';
import { LocationForm } from '@/pages/LocationForm';
import {
  createStorageLocation,
  deleteStorageLocation,
  describeApiError,
  listStorageLocations,
  updateStorageLocation,
  uploadStorageLocationPhoto,
  type StorageLocation,
  type StorageLocationInput
} from '@/shared/storage-locations-api';
import { fetchDepartments, fetchLocalities, type Department, type LocalitiesByDepartment } from '@/shared/locations-catalog';
import { getMyServices, getServiceOptions, type ServiceOption } from '@/shared/storage-services-api';
import { serviceOptionsOnly } from '@/shared/service-option-kinds';
import { getStorageProfile } from '@/shared/storage-api';
import { notifyNavCountsChanged } from '@/shared/nav-counts-signal';
import { getAddressSearch } from '@/shared/google-maps-options';
import { LocationCard } from '@/shared/LocationCard';
import { CheckIcon, LockersIcon } from '@/shared/icons';
import { CHIP_WRAP } from '@/shared/chip-wrap';

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; location: StorageLocation };

/**
 * Roadmap §4 Milestone 3 — CRUD de ubicaciones contra /storage/locations (spec §2, §4).
 *
 * **Presentational rebuild, NEX-1164**: the list below now renders through `LocationCard`
 * (`src/shared/`) and the real `Chip` instead of a hand-rolled `<ul>` of bordered rows, to match
 * the Claude Design mockup for this screen. All create/edit/delete business logic and API calls
 * below are unchanged from the pre-rebuild version — only the "locations, departments, localities
 * loaded, now render them" branch at the bottom of this component changed shape.
 *
 * **NEX-1164 (2026-10-15)**: the page chrome is the real `Page` + `PageHeader` (title, description
 * and the "Agregar depósito" action), the loading line a `Spinner`, the empty state an `EmptyState`
 * (no action of its own: the header already holds the one "Agregar depósito" button) and the grid
 * `md:grid-cols-2` (the preset declares no `sm:`). Not `ListPage`, on purpose (spec D-4.1): a
 * photo-card grid inside its white panel would be cards on a card.
 *
 * **NEX-1151 design-system migration (2026-10-14)**: classNames below now use the real
 * `@nexvia-logistics/ui`/`@nexvia-logistics/tokens` vocabulary instead of the retired `nx`-prefixed
 * placeholder. The "Agregar depósito" action is now the real `Button` atom (`variant="primary"`,
 * `size="xs"` — the 44px product control height used across this app's other migrated pages;
 * `showArrow={false}` because `Button` defaults to showing its marketing trailing arrow, which this
 * product surface never wants). The load error banner is the real `Alert` atom (a failed delete
 * shows in the edit form's own one since the review fix) (`tone="critical"`, which is what gives it
 * `role="alert"` — `Alert` renders `role="status"` for every other tone, so this is required, not
 * cosmetic, to keep this page's existing `getByRole('alert')` test assertions passing).
 *
 * **NEX-1164 mockup-parity fix**: card titles now read "{razonSocial} · {locality}" (mockup:
 * "Rivera · Pocitos") instead of just "{locality}, {department}". `businessName` is fetched via a
 * third, independent effect (see `getStorageProfile`'s own docblock in `@/shared/storage-api` for
 * why that call lives there and not a cross-feature import of `src/pages/empresa/storage-api.ts`'s
 * same-named function) — same defensive philosophy as the service-badges effect below: a failure
 * fetching it, or an empty/whitespace-only `razonSocial`, must never block or blank this page, so
 * the title falls back to the original "{locality}, {department}" format in either case. Additive
 * only — never a new failure mode for this page.
 *
 * **NEX-1164 design pass (2026-10-18, spec §3.4)**: the mock's description, cards with only
 * "Editar" (`LocationCard`), neutral service chips with a check, and "Eliminar depósito" moved into
 * the edit form (`LocationForm`'s `onRemove`, decision D-4.1) — same `window.confirm`, same
 * `deleteStorageLocation`.
 */
export default function DepositosPage() {
  const [locations, setLocations] = useState<StorageLocation[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [localitiesByDepartment, setLocalitiesByDepartment] = useState<LocalitiesByDepartment>({});
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  // Storage responsive pass (2026-10-27): list and form share one route, so the router restores nothing;
  // without this, "Editar" on a card far down the list opened the form past its title.
  const modeKey = mode.kind === 'edit' ? `edit-${mode.location.id}` : mode.kind;
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [modeKey]);
  /**
   * [PENDING-DECISION] Deliberate interim resolution, not an oversight: this app's services/
   * conditions are modeled ACCOUNT-WIDE (`GET /storage/me/services`, the same source
   * `ServiciosPage.tsx` already reads/writes via `getMyServices`/`updateMyServices`) — there is no
   * per-location services column on `storage_locations` (see `src/contracts/storage-locations.ts`;
   * its `StorageLocationSchema` has no such field). Every card below therefore shows the SAME set of
   * badges (this account's offered services), not a per-location curation. True per-location service
   * curation would need a backend schema change (a new column + contract) if the product ever wants
   * that instead of the account-wide model.
   */
  const [serviceBadges, setServiceBadges] = useState<ServiceOption[]>([]);
  // NEX-1164 mockup parity: "" means "no confirmed business name yet" (initial render, fetch still
  // pending, fetch failed, or the profile's razonSocial was empty/whitespace-only) — every one of
  // those cases falls back to the pre-existing "{locality}, {department}" card title below, never a
  // blank/broken one.
  const [businessName, setBusinessName] = useState('');
  // NEX-1168 (plan 2026-10-20 Tarea 2.7) — Google's address search for the form, or `null` without
  // `VITE_APP_GOOGLE_MAPS_API_KEY` (the address is then typed by hand, with no coordinates).
  const [addressSearch] = useState(getAddressSearch);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [locationList, departmentList, localityMap] = await Promise.all([
          listStorageLocations(),
          fetchDepartments(),
          fetchLocalities()
        ]);
        if (cancelled) return;
        // Defensive: a backend/contract mismatch must show the existing error banner, not leave
        // `locations` holding something non-array that crashes every state update below (this is
        // exactly how the 2026-10-14 "(current ?? []) is not iterable" crash happened — a stale
        // pre-fix envelope object reached here and `locations` was never actually an array).
        if (!Array.isArray(locationList)) {
          setLoadError(describeApiError(new Error('Unexpected /storage/locations response shape')));
          return;
        }
        setLocations(locationList);
        setDepartments(departmentList);
        setLocalitiesByDepartment(localityMap);
      } catch (error) {
        if (!cancelled) setLoadError(describeApiError(error));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Deliberately a SEPARATE effect/state from the load() above, not folded into the same
  // Promise.all: badges are a read-only embellishment on top of the real location list, and a
  // failure fetching them must never block or blank the page the location list itself renders on
  // (same defensive philosophy as `asArray` below, applied to a second, independent data source).
  useEffect(() => {
    let cancelled = false;

    async function loadServiceBadges() {
      try {
        const [catalog, mine] = await Promise.all([getServiceOptions(), getMyServices()]);
        if (cancelled) return;
        const offeredCodes = new Set(mine.offeredServiceCodes);
        // NEX-1165 — services only (catalog `kind` 'service'): the account's selection also holds
        // the conditions it imposes, which are not something the location offers.
        setServiceBadges(serviceOptionsOnly(catalog).filter((option) => offeredCodes.has(option.code)));
      } catch {
        if (!cancelled) setServiceBadges([]);
      }
    }

    loadServiceBadges();
    return () => {
      cancelled = true;
    };
  }, []);

  // Deliberately a THIRD, separate effect/state from load()/loadServiceBadges() above, same
  // defensive philosophy: the business name is a read-only embellishment on the card title (NEX-1164
  // mockup parity), and a failure fetching it must never block or blank the page the location list
  // itself renders on. `.trim()` so a whitespace-only `razonSocial` (never actually empty per the
  // backend's own NOT NULL constraint, but not guaranteed non-blank either) falls back the same way
  // a missing one does, rather than rendering a title like " · Rural".
  useEffect(() => {
    let cancelled = false;

    async function loadBusinessName() {
      try {
        const profile = await getStorageProfile();
        if (cancelled) return;
        setBusinessName(profile.razonSocial.trim());
      } catch {
        if (!cancelled) setBusinessName('');
      }
    }

    loadBusinessName();
    return () => {
      cancelled = true;
    };
  }, []);

  // `current ?? []` alone only replaces null/undefined — a truthy non-array (e.g. a stale envelope
  // object left over from a shape mismatch) would reach `[...current]`/`.map`/`.filter` below and
  // crash the whole page, which is exactly what happened in the running app on 2026-10-14. This
  // coerces any non-array to [] instead, so a backend/contract surprise degrades to "empty list",
  // never a crash.
  const asArray = (current: StorageLocation[] | null): StorageLocation[] => (Array.isArray(current) ? current : []);

  // Each saved change also refreshes the sidebar badges (audit STO-9): Mis depósitos counts the
  // locations, and Solicitudes is the inbox for their zones, so an edit can change it too.
  const handleCreate = async (input: StorageLocationInput) => {
    const created = await createStorageLocation(input);
    setLocations((current) => [...asArray(current), created]);
    notifyNavCountsChanged();
    setMode({ kind: 'list' });
  };

  const handleUpdate = async (id: number, input: StorageLocationInput) => {
    const updated = await updateStorageLocation(id, input);
    setLocations((current) => asArray(current).map((item) => (item.id === id ? updated : item)));
    notifyNavCountsChanged();
    setMode({ kind: 'list' });
  };

  // NEX-1164 design pass (spec 2026-10-18 §3.4, D-4.1): called from the edit form's "Eliminar
  // depósito" now, not from the card. Same confirmation and call. A deleted location goes back to
  // the shorter list; a dismissed confirmation stays in the form. Review fix: a failed delete
  // rejects, and the form shows the error (`describeApiError`) without discarding unsaved edits.
  const handleDelete = async (location: StorageLocation) => {
    const confirmed = window.confirm(`¿Seguro que querés eliminar el depósito en ${location.locality}, ${location.department}?`);
    if (!confirmed) return;

    await deleteStorageLocation(location.id);
    setLocations((current) => asArray(current).filter((item) => item.id !== location.id));
    notifyNavCountsChanged();
    setMode({ kind: 'list' });
  };

  if (mode.kind === 'create') {
    return (
      <LocationForm
        departments={departments}
        localitiesByDepartment={localitiesByDepartment}
        onUploadPhoto={uploadStorageLocationPhoto}
        onSubmit={handleCreate}
        onCancel={() => setMode({ kind: 'list' })}
        addressSearch={addressSearch}
      />
    );
  }

  if (mode.kind === 'edit') {
    const { location } = mode;
    return (
      <LocationForm
        initialValue={location}
        departments={departments}
        localitiesByDepartment={localitiesByDepartment}
        onUploadPhoto={uploadStorageLocationPhoto}
        onSubmit={(input) => handleUpdate(location.id, input)}
        onCancel={() => setMode({ kind: 'list' })}
        onRemove={() => handleDelete(location)}
        addressSearch={addressSearch}
      />
    );
  }

  return (
    <Page className="py-6">
      <PageHeader
        title="Mis depósitos"
        description="Se cargan una sola vez. La dirección y las fotos de la ubicación que elijas viajan con cada cotización."
        actions={
          <Button type="button" variant="primary" size="xs" showArrow={false} onClick={() => setMode({ kind: 'create' })}>
            <Plus size={16} aria-hidden="true" />
            Agregar depósito
          </Button>
        }
      />

      {loadError && <Alert tone="critical">{loadError}</Alert>}

      {locations === null && !loadError && (
        <p className="m-0 flex items-center gap-2 text-ui-body text-on-surface-variant">
          <Spinner size={18} label={null} />
          Cargando tus depósitos…
        </p>
      )}

      {locations !== null && locations.length === 0 && (
        <EmptyState icon={<LockersIcon size={24} />} title="Todavía no cargaste ningún depósito." />
      )}

      {locations !== null && locations.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {locations.map((location) => (
            <LocationCard
              key={location.id}
              name={businessName ? `${businessName} · ${location.locality}` : `${location.locality}, ${location.department}`}
              lines={[location.address]}
              onEdit={() => setMode({ kind: 'edit', location })}
              photos={location.photos}
              badges={
                serviceBadges.length > 0 ? (
                  <>
                    {serviceBadges.map((service) => (
                      <Chip key={service.code} tone="neutral" icon={<CheckIcon size={12} />} className={CHIP_WRAP}>
                        {service.label}
                      </Chip>
                    ))}
                  </>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </Page>
  );
}
